import { useCallback, useSyncExternalStore } from "react";

// The design package timeline's view switches: "Full timeline" in the lane
// header and the legend's Start lines, Link Lines and Date under cursor. One
// choice for every design package: kept in memory, so it carries from one
// design package to the next (and to every lane on screen at once) and goes
// back to the defaults on a reload.

export type TimelineViewPrefs = {
  fullTimeline: boolean;
  startLines: boolean;
  linkLines: boolean;
  dateCursor: boolean;
};

export const TIMELINE_VIEW_PREF_DEFAULTS: TimelineViewPrefs = {
  fullTimeline: true,
  startLines: false,
  linkLines: true,
  dateCursor: true
};

let prefs: TimelineViewPrefs = TIMELINE_VIEW_PREF_DEFAULTS;
const listeners = new Set<() => void>();

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function setPref(key: keyof TimelineViewPrefs, value: boolean) {
  if (prefs[key] === value) return;
  prefs = { ...prefs, [key]: value };
  listeners.forEach((listener) => listener());
}

/** Back to the defaults, as on a reload. */
export function resetTimelineViewPrefs(): void {
  prefs = TIMELINE_VIEW_PREF_DEFAULTS;
  listeners.forEach((listener) => listener());
}

/** One switch's value and its setter, shared by every timeline. */
export function useTimelineViewPref(
  key: keyof TimelineViewPrefs
): [boolean, (value: boolean) => void] {
  const value = useSyncExternalStore(
    subscribe,
    () => prefs[key],
    () => TIMELINE_VIEW_PREF_DEFAULTS[key]
  );
  const set = useCallback((next: boolean) => setPref(key, next), [key]);
  return [value, set];
}
